import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Loader2 } from "lucide-react";

/**
 * Route guard that redirects to /login only on unauthenticated errors.
 * Transient service errors (API down, network issues) are shown inline —
 * the retry logic in AuthProvider already retried before reaching here.
 */
export function AuthGuard() {
  const { auth, isLoading, error, errorCode } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Auth succeeded — pass through
  if (auth) {
    return <Outlet />;
  }

  // Session missing/expired — redirect to login
  if (errorCode === "unauthenticated") {
    const target = `${location.pathname}${location.search}`;
    const parts: string[] = [];
    if (target !== "/" && location.pathname !== "/login") {
      parts.push(`redirect=${encodeURIComponent(target)}`);
    }
    const qs = parts.length > 0 ? `?${parts.join("&")}` : "";
    return <Navigate to={`/login${qs}`} replace />;
  }

  // Service error — show error (retries already exhausted)
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="text-center space-y-2">
        <p className="text-lg font-medium text-destructive">Connection error</p>
        <p className="text-sm text-muted-foreground">
          {error ?? "Failed to connect to the API"}
        </p>
        <p className="text-xs text-muted-foreground">
          Make sure the API server is running.
        </p>
      </div>
    </div>
  );
}
